/**
 * VER-76 — MoodScaleSelector
 *
 * A reusable 1–5 toggle-button mood rating selector.
 * Designed to be quick to complete mid-session: five large labelled buttons,
 * one tap/click, consistent with the ToggleInput pattern already in the project.
 *
 * Deliberately emoji-free. Labels follow a plain language low→high axis
 * (Very low → Low → Okay → Good → Very good) to keep the interface
 * accessible and screen-reader friendly.
 *
 * NOT a clinical instrument. WEMWBS lives in the SurveyMonkey flow (S1/S6).
 */

import React from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { MoodScore } from "./mood.types";

const MOOD_LABELS: Record<MoodScore, string> = {
    1: "Very low",
    2: "Low",
    3: "Okay",
    4: "Good",
    5: "Very good",
};

type MoodScaleSelectorProps = {
    value: MoodScore | null;
    onChange: (score: MoodScore) => void;
    disabled?: boolean;
    /** Accessible label for the group — announced by screen readers. */
    ariaLabel?: string;
};

export const MoodScaleSelector = ({
    value,
    onChange,
    disabled = false,
    ariaLabel = "Mood rating scale",
}: MoodScaleSelectorProps): JSX.Element => {
    const scores: MoodScore[] = [1, 2, 3, 4, 5];

    /**
     * An `exclusive` group reports `null` when the already-selected button is clicked again — its
     * deselect gesture. Here that click means "record this rating again", which is exactly what a
     * practitioner does after a failed save ("Not saved — select again to retry"): they press the
     * same number. Swallowing the null left that instruction impossible to follow, and there is no
     * unrated state worth reaching by clicking, so a re-click re-emits the current score instead.
     */
    const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
        const score = newValue ?? value;
        if (score !== null) {
            onChange(score as MoodScore);
        }
    };

    return (
        <Box>
            <ToggleButtonGroup
                value={value}
                exclusive
                onChange={handleChange}
                aria-label={ariaLabel}
                disabled={disabled}
                sx={{
                    width: "100%",
                    display: "flex",
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
            >
                {scores.map((score) => (
                    <ToggleButton
                        key={score}
                        value={score}
                        aria-label={`${score} — ${MOOD_LABELS[score]}`}
                        sx={{
                            flex: 1,
                            minWidth: { xs: "calc(50% - 4px)", sm: "auto" },
                            flexDirection: "column",
                            gap: 0.5,
                            py: 1.5,
                            px: 1,
                            borderRadius: (theme) => `${theme.shape.borderRadius}px !important`,
                            border: (theme) => `1px solid ${theme.palette.primary.main} !important`,
                        }}
                    >
                        <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
                            {score}
                        </Typography>
                        <Typography variant="caption" component="span" sx={{ lineHeight: 1.2, textTransform: "none" }}>
                            {MOOD_LABELS[score]}
                        </Typography>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Box display="flex" justifyContent="space-between" mt={0.75} px={0.25}>
                <Typography variant="caption" color="textSecondary">
                    1 = lowest
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    5 = highest
                </Typography>
            </Box>
        </Box>
    );
};
